import type { PricingEdition } from '@highlight/common'
import {
  planDiscountNote,
  PlanPrice,
  planTierLabel,
  type PlanDiscount,
  type SelectableV2Plan,
} from './plan-select-cards'
import { CardOnFileCta, PurchaseCta, SubscriptionDialog } from './subscription-dialog'

export interface PlanEndedPaywallProps {
  plan: SelectableV2Plan
  /** The lineup whose price the tier is renewed at. */
  edition: PricingEdition
  /** A discount the account holds, so the renewal is sold at the price it will bill. */
  discount?: PlanDiscount | null
  onRenew: () => void
  isPending?: boolean
  errorMessage?: string | null
  className?: string
}

/**
 * The plan-ended variant of the same strip: the tier's monthly price and a
 * Renew CTA. The design pairs the price with a Pay-annually toggle; that's
 * deliberately omitted, so renewal is sold monthly only.
 */
export function PlanEndedPaywall({
  plan,
  edition,
  discount = null,
  onRenew,
  isPending,
  errorMessage,
  className,
}: PlanEndedPaywallProps) {
  return (
    <SubscriptionDialog
      plan={plan}
      title="Keep doing your best work"
      message={`Your ${planTierLabel(plan)} plan has ended. Renew to keep your custom skills, drafted actions, and more working for you.`}
      className={className}
    >
      <MonthlyPrice plan={plan} edition={edition} discount={discount} />

      <div className="flex flex-col gap-3">
        {errorMessage && <p className="text-red m-0 text-center text-[13px]">{errorMessage}</p>}
        <CardOnFileCta plan={plan} label="Renew" isPending={isPending} onClick={onRenew} />
      </div>
    </SubscriptionDialog>
  )
}

/**
 * The tier's monthly price, shared by the renewal and upgrade footers. The row
 * is the design's fixed 20px, so the footer's own 16px gap is the whole
 * distance down to the CTA. Padding here reads as a wider gap than the spec.
 */
function MonthlyPrice({
  plan,
  edition,
  discount,
}: {
  plan: SelectableV2Plan
  edition: PricingEdition
  discount: PlanDiscount | null
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex h-5 items-center">
        <div className="flex items-end gap-1.5">
          <PlanPrice plan={plan} edition={edition} discount={discount} size="paywall" />
          <span className="text-[13px] font-medium leading-none">
            <span className="text-foreground-placeholder">/ </span>
            <span className="text-foreground-secondary">per month</span>
          </span>
        </div>
      </div>
      {discount && (
        <span className="text-foreground-secondary text-[12px]">{planDiscountNote(plan, edition, discount)}</span>
      )}
    </div>
  )
}

export interface PlanUpgradePaywallProps {
  /** The tier being sold. Free and legacy plans hold none of their own, so the caller passes the upgrade target. */
  plan: SelectableV2Plan
  /** A discount the account holds, so the upgrade is sold at the price it will bill. */
  discount?: PlanDiscount | null
  /** The lineup whose price the tier is sold at. */
  edition: PricingEdition
  title?: string
  message?: string
  /** Heads the dialog with the tier's logo tile, as the modal does. */
  brandMark?: boolean
  onUpgrade: () => void
  /** Whether a card is already on file, which decides whether the CTA promises a charge or opens checkout. */
  hasPaymentMethod?: boolean
  isPending?: boolean
  errorMessage?: string | null
  className?: string
}

/**
 * The acquisition variant of the subscription strip: the tier's monthly price
 * and an Upgrade CTA. Shown to the plans that hold no tier of their own, which
 * is why it sells a plan rather than a top-up against one. Its callers narrow
 * that to free: a paid legacy plan holds no tier either but needs nothing.
 */
export function PlanUpgradePaywall({
  plan,
  edition,
  discount = null,
  title = 'Do your best work with Highlight',
  message = `Upgrade to ${planTierLabel(plan)} to transcribe your meetings, keep your briefs and recaps coming, and chat without limits.`,
  brandMark,
  onUpgrade,
  hasPaymentMethod = false,
  isPending,
  errorMessage,
  className,
}: PlanUpgradePaywallProps) {
  const label = `Upgrade to ${planTierLabel(plan)}`
  return (
    <SubscriptionDialog plan={plan} title={title} message={message} brandMark={brandMark} className={className}>
      <MonthlyPrice plan={plan} edition={edition} discount={discount} />

      <div className="flex flex-col gap-3">
        {errorMessage && <p className="text-red m-0 text-center text-[13px]">{errorMessage}</p>}
        {hasPaymentMethod ? (
          <CardOnFileCta plan={plan} label={label} isPending={isPending} onClick={onUpgrade} />
        ) : (
          <PurchaseCta plan={plan} label={label} isPending={isPending} onClick={onUpgrade} />
        )}
      </div>
    </SubscriptionDialog>
  )
}

export interface TrialEndingPaywallProps {
  plan: SelectableV2Plan
  /** The lineup whose price the tier is quoted at. */
  edition: PricingEdition
  /** A discount the account holds, so the tier is quoted at the price it will bill after the trial. */
  discount?: PlanDiscount | null
  /** Whole days until the trial ends; zero on its last day. */
  daysLeft: number
  onKeep: () => void
  isPending?: boolean
  errorMessage?: string | null
  className?: string
}

/** "ends today" / "ends tomorrow" / "ends in N days". */
export function trialEndingTitle(label: string, daysLeft: number): string {
  if (daysLeft <= 0) return `Your ${label} trial ends today`
  if (daysLeft === 1) return `Your ${label} trial ends tomorrow`
  return `Your ${label} trial ends in ${daysLeft} days`
}

/**
 * The trial-ending variant: a trial started without a card is about to end on
 * the free plan, so this asks for the card that keeps the tier. The CTA opens
 * checkout, which only saves the card; the trial runs out its remaining days
 * before the first charge, so the CTA promises none.
 */
export function TrialEndingPaywall({
  plan,
  edition,
  discount = null,
  daysLeft,
  onKeep,
  isPending,
  errorMessage,
  className,
}: TrialEndingPaywallProps) {
  const label = planTierLabel(plan)
  return (
    <SubscriptionDialog
      plan={plan}
      title={trialEndingTitle(label, daysLeft)}
      message={`Add a payment method to keep ${label} when your trial ends. Without one you'll move to the free plan.`}
      className={className}
    >
      <MonthlyPrice plan={plan} edition={edition} discount={discount} />

      <div className="flex flex-col gap-3">
        {errorMessage && <p className="text-red m-0 text-center text-[13px]">{errorMessage}</p>}
        <PurchaseCta plan={plan} label={`Keep ${label}`} isPending={isPending} onClick={onKeep} />
      </div>
    </SubscriptionDialog>
  )
}
